/**
 * StepCard — one step in the Resume Workspace workflow, rendered in one of
 * three states derived from the target detail by `stepState`:
 *
 *   locked     — prereq not yet run. Muted card naming the blocking step.
 *   available  — runnable now. Optional input field + a Run button. The button
 *                is always enabled in this state; required-input validation
 *                happens on click (so the user gets a clear inline error rather
 *                than a silently-disabled button).
 *   done       — already run. Shows the step's narrative output and a Re-run
 *                affordance that drops back to the available form.
 *
 * The Run mutation hits POST .../steps/{key}; we map the backend's failure
 * modes to friendly UI: 429 → upgrade prompt, 502 → bad-format retry hint,
 * 422 (MISSING_INPUT) → inline field error. onSuccess bubbles to `onRan` so the
 * parent can refetch the target (which flips this card to "done").
 *
 * Output rendering (done state) is delegated to per-step structured renderers
 * via renderStepOutput; each renderer also appends the step's narrative.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import ReplayIcon from "@mui/icons-material/Replay";
import { useMutation } from "@tanstack/react-query";
import { runResumeStep } from "../../api/client";
import type { ResumeTargetDetail } from "../../api/client";
import { STEP_CONFIG, stepState } from "./stepConfig";
import type { StepConfig } from "./stepConfig";
import { renderStepOutput } from "./outputs";
import { useAuth } from "../../hooks/useAuth";
import { getTierAccess } from "../../utils/tier";
import ProUpsellNote from "../ProUpsellNote";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

interface StepCardProps {
  cfg: StepConfig;
  target: ResumeTargetDetail;
  onRan: () => void;
}

/** Map an axios error from the run endpoint to a friendly message + intent. */
function classifyRunError(
  err: any,
  t: TFunction
): {
  kind: "quota" | "format" | "missing_input" | "generic";
  message: string;
} {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const code = typeof detail === "object" ? detail?.code : undefined;

  if (status === 429) {
    return {
      kind: "quota",
      message: t("StepCard.quotaMessage"),
    };
  }
  if (status === 502) {
    return {
      kind: "format",
      message: t("StepCard.formatMessage"),
    };
  }
  if (status === 422 && code === "MISSING_INPUT") {
    return {
      kind: "missing_input",
      message:
        (typeof detail === "object" && detail?.message) ||
        t("StepCard.missingInputFallback"),
    };
  }
  const fallback =
    (typeof detail === "object" && detail?.message) ||
    (typeof detail === "string" && detail) ||
    t("StepCard.genericFallback");
  return { kind: "generic", message: fallback };
}

export default function StepCard({ cfg, target, onRan }: StepCardProps) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const state = stepState(cfg, target);
  const { user } = useAuth();
  const tierAccess = getTierAccess(user);

  // "Re-run" reveals the available-state form even though the step is done.
  const [rerunning, setRerunning] = useState(false);

  // Tier gate (e.g. interview_readiness is All-Access only). Takes precedence
  // over prereq/availability so the upsell always shows for a gated step.
  const tierLocked = cfg.minTier === "all_access" && !tierAccess.canAccessProducts;
  if (tierLocked) {
    if (tierAccess.isPro) {
      return <ProUpsellNote />;
    }
    return (
      <Card
        variant="outlined"
        sx={{ borderColor: alpha(theme.palette.primary.light, 0.12), borderRadius: 3, bgcolor: alpha(theme.palette.primary.light, 0.02) }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <LockIcon sx={{ fontSize: 20, color: alpha(theme.palette.primary.main, 0.5) }} />
            <Box sx={{ flexGrow: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" fontWeight={500} color="text.secondary">
                  {cfg.label}
                </Typography>
                <Chip
                  size="small"
                  label={t("StepCard.allAccessChip")}
                  sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.primary.light, 0.12), color: theme.palette.primary.main }}
                />
              </Stack>
              <Typography variant="body2" color="text.disabled">
                {t("StepCard.upgradeToUnlock")}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (state === "locked") {
    const prereqLabel =
      STEP_CONFIG.find((c) => c.key === cfg.prereq)?.label ?? cfg.prereq;
    return (
      <Card
        variant="outlined"
        sx={{
          borderColor: alpha(theme.palette.primary.light, 0.12),
          borderRadius: 3,
          bgcolor: alpha(theme.palette.primary.light, 0.02),
          opacity: 0.85,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <LockIcon sx={{ fontSize: 20, color: alpha(theme.palette.primary.main, 0.5) }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={500} color="text.secondary">
                {cfg.label}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {t("StepCard.runPrereqFirst", { prereq: prereqLabel })}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // ----- done (and not re-running) -----------------------------------------
  if (state === "done" && !rerunning) {
    const step = target.steps[cfg.key];
    return (
      <Card
        variant="outlined"
        sx={{ borderColor: alpha(theme.palette.success.main, 0.3), borderRadius: 3, bgcolor: alpha(theme.palette.success.main, 0.02) }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1.5}
            mb={1.5}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <CheckCircleIcon sx={{ fontSize: 22, color: theme.palette.success.main }} />
              <Typography variant="subtitle1" fontWeight={500}>
                {cfg.label}
              </Typography>
            </Stack>
            <Button
              size="small"
              startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
              onClick={() => setRerunning(true)}
              sx={{
                color: theme.palette.primary.main,
                textTransform: "none",
                fontWeight: 500,
                "&:hover": { bgcolor: alpha(theme.palette.primary.light, 0.06) },
              }}
            >
              {t("StepCard.rerun")}
            </Button>
          </Stack>
          {renderStepOutput(cfg.key, step)}
        </CardContent>
      </Card>
    );
  }

  // ----- available (or done + re-running) ----------------------------------
  return (
    <AvailableStep
      cfg={cfg}
      targetId={target.id}
      onRan={onRan}
      onCancel={state === "done" ? () => setRerunning(false) : undefined}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Available step — the runnable form (also used for the done→re-run path)     */
/* -------------------------------------------------------------------------- */

function AvailableStep({
  cfg,
  targetId,
  onRan,
  onCancel,
}: {
  cfg: StepConfig;
  targetId: string;
  onRan: () => void;
  onCancel?: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation("resume");
  const [value, setValue] = useState("");
  // Client-side required-input error (kept separate from server errors so a
  // server MISSING_INPUT and a local one share the field-error rendering).
  const [localError, setLocalError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      runResumeStep(targetId, cfg.key, cfg.inputKey ? { [cfg.inputKey]: value } : {}),
    onSuccess: () => onRan(),
  });

  const requiredMissing =
    !!cfg.inputKey && !cfg.inputOptional && value.trim().length === 0;

  const errInfo = mutation.isError ? classifyRunError(mutation.error, t) : null;

  const handleRun = () => {
    if (requiredMissing) {
      // Surface the validation as an inline field error without calling the API.
      setLocalError(t("StepCard.fillFieldRequired"));
      return;
    }
    setLocalError(null);
    mutation.mutate();
  };

  const fieldError =
    localError ?? (errInfo?.kind === "missing_input" ? errInfo.message : null);

  const pendingLabel = t("StepCard.running");

  return (
    <Card variant="outlined" sx={{ borderColor: alpha(theme.palette.primary.light, 0.25), borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" spacing={1.25} alignItems="center" mb={cfg.inputKey ? 1.75 : 1.5}>
          <Typography variant="subtitle1" fontWeight={500}>
            {cfg.label}
          </Typography>
          {cfg.inputOptional && (
            <Chip
              label={t("StepCard.optionalInput")}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 500,
                letterSpacing: "0.04em",
                bgcolor: alpha(theme.palette.primary.light, 0.1),
                color: theme.palette.primary.main,
              }}
            />
          )}
        </Stack>

        {cfg.inputKey && (
          <TextField
            label={cfg.inputLabel}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (localError) setLocalError(null);
            }}
            fullWidth
            multiline
            minRows={3}
            maxRows={12}
            disabled={mutation.isPending}
            error={!!fieldError}
            helperText={fieldError ?? undefined}
            sx={{ mb: 1.75 }}
          />
        )}

        {/* Non-field-level errors (quota / format / generic) */}
        {errInfo && errInfo.kind === "quota" && (
          <Alert
            severity="warning"
            sx={{ mb: 1.75 }}
            action={
              <Button
                color="inherit"
                size="small"
                href="/pricing"
                sx={{ fontWeight: 500, textTransform: "none" }}
              >
                {t("StepCard.upgradeButton")}
              </Button>
            }
          >
            {errInfo.message}
          </Alert>
        )}
        {errInfo && (errInfo.kind === "format" || errInfo.kind === "generic") && (
          <Alert severity="error" sx={{ mb: 1.75 }}>
            {errInfo.message}
          </Alert>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {onCancel && (
            <Button
              onClick={onCancel}
              disabled={mutation.isPending}
              sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}
            >
              {t("StepCard.cancel")}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleRun}
            disabled={mutation.isPending}
            startIcon={
              mutation.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              fontWeight: 500,
              textTransform: "none",
              px: 3,
            }}
          >
            {mutation.isPending ? pendingLabel : t("StepCard.run")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
